export type Vec3 = Readonly<{ x: number; y: number; z: number }>;
export type Quat = Readonly<{ x: number; y: number; z: number; w: number }>;
export type EntityId = number;
export type Team = 'blue' | 'orange';

export interface SpatialListenerPose {
  readonly position: Vec3;
  readonly forward: Vec3;
  readonly up: Vec3;
}

export interface Controls {
  throttle: number;
  steer: number;
  pitch: number;
  yaw: number;
  roll: number;
  jump: boolean;
  boost: boolean;
  handbrake: boolean;
}

export const NEUTRAL_CONTROLS: Controls = Object.freeze({
  throttle: 0,
  steer: 0,
  pitch: 0,
  yaw: 0,
  roll: 0,
  jump: false,
  boost: false,
  handbrake: false,
});

export const controlsEqual = (left: Controls, right: Controls): boolean =>
  left.throttle === right.throttle && left.steer === right.steer
  && left.pitch === right.pitch && left.yaw === right.yaw && left.roll === right.roll
  && left.jump === right.jump && left.boost === right.boost && left.handbrake === right.handbrake;

export interface BodyState {
  position: Vec3;
  velocity: Vec3;
  angularVelocity: Vec3;
  rotation: Quat;
}

export interface CarState extends BodyState {
  boost: number;
  grounded: boolean;
  demolished: boolean;
  hasJump: boolean;
  hasFlip: boolean;
  hasJumped: boolean;
  hasDoubleJumped: boolean;
  hasFlipped: boolean;
  isJumping: boolean;
  isFlipping: boolean;
  isBoosting: boolean;
  worldContact: boolean;
  worldContactNormal: Vec3;
  lastBallHitTick: number;
  supersonic: boolean;
}

export type BallState = BodyState;

export interface BoostPadState {
  position: Vec3;
  isBig: boolean;
  available: boolean;
  cooldownSeconds: number;
}

export interface VehicleState {
  readonly entityId: EntityId;
  readonly team: Team;
  readonly car: CarState;
}

export interface TeamScore {
  readonly blue: number;
  readonly orange: number;
}

export type MatchPhase = 'preparation' | 'kickoff-countdown' | 'active' | 'goal-celebration' | 'goal-replay' | 'finished';
export type KickoffCue = 3 | 2 | 1 | 'GO';

export interface MatchClockState {
  readonly durationSeconds: number | null;
  readonly playedTicks: number;
  readonly overtimeTicks: number;
  readonly remainingSeconds: number | null;
  readonly overtime: boolean;
}

export interface MatchResult {
  readonly winner: Team | null;
  readonly score: TeamScore;
  readonly reason: 'time' | 'golden-goal' | 'forfeit' | 'abandonment' | 'cancelled';
}

export interface MatchFlowState {
  readonly phase: MatchPhase;
  readonly kickoffCue: KickoffCue | null;
  readonly goalTeam: Team | null;
  readonly kickoffActive: boolean;
}

export interface MatchState {
  tick: number;
  timeSeconds: number;
  score: TeamScore;
  flow: MatchFlowState;
  clock: MatchClockState;
  result: MatchResult | null;
  vehicles: readonly VehicleState[];
  ball: BallState;
  boostPads: readonly BoostPadState[];
}

export type MatchTeamSize = 1 | 2 | 3;

export type MatchConfig =
  | Readonly<{ mode: 'freeplay' }>
  | Readonly<{
    mode: 'exhibition';
    teamSize: MatchTeamSize;
    localTeam: Team | null;
    bots: readonly Readonly<{ botId: string; team: Team }>[];
  }>;

export interface PhysicsSnapshot {
  vehicles: readonly VehicleState[];
  ball: BallState;
  boostPads: readonly BoostPadState[];
}

export type PhysicsEvent =
  | Readonly<{ type: 'ball-ground-contact' }>
  | Readonly<{
    type: 'boost-pickup';
    entityId: EntityId;
    team: Team;
    padIndex: number;
    isBig: boolean;
  }>
  | Readonly<{
    type: 'goal';
    team: Team;
  }>
  | Readonly<{
    type: 'ball-hit';
    entityId: EntityId;
    team: Team;
    position: Vec3;
    normal: Vec3;
    intensity: number;
  }>
  | Readonly<{
    type: 'car-hit';
    entityA: EntityId;
    entityB: EntityId;
    position: Vec3;
    normal: Vec3;
    intensity: number;
  }>
  | Readonly<{
    type: 'demolition';
    entityId: EntityId;
    team: Team;
    position: Vec3;
  }>
  | Readonly<{
    type: 'flip-reset';
    entityId: EntityId;
    team: Team;
    position: Vec3;
  }>;

export function vehicleById(state: Pick<MatchState | PhysicsSnapshot, 'vehicles'>, entityId: EntityId): VehicleState {
  const vehicle = state.vehicles.find((candidate) => candidate.entityId === entityId);
  if (!vehicle) throw new Error(`Vehicle entity ${entityId} is not present in the world snapshot.`);
  return vehicle;
}

export function opposingVehicle(state: Pick<MatchState, 'vehicles'>, entityId: EntityId): VehicleState {
  const self = vehicleById(state, entityId);
  const opponent = state.vehicles.find((candidate) => candidate.team !== self.team);
  if (!opponent) throw new Error(`Vehicle entity ${entityId} has no opposing vehicle in this match.`);
  return opponent;
}
